<template>
  <div class="lp-number-field-wrapper">
    <div class="ly-row ly-spacebetween-center">
      <span v-if="labelTop" class="lp-input-label font-weight-medium">
        <span>{{ labelTop }}</span>
        <span v-if="!preview && required" class="text-blue">*</span>
      </span>
      <span>
        <span v-if="options && showOptionValue">{{ optionLabelRef }}</span>
        <div v-if="$slots?.topActions" class="xmb-2">
          <slot name="topActions" />
        </div>
      </span>
    </div>
    <div class="lp-number-field">
      <div
        class="lp-number-input lp-input-wrapper ly-row ly-center-center ly-no-gap"
        :class="[
          disabled && 'lp-number-input-disabled',
          message
            ? `lp-input-wrapper-danger`
            : status && `lp-input-wrapper-${status}`,
          disabled && `lp-input-wrapper-disabled`,
          preview && 'lp-number-input-preview',
        ]"
      >
        <span
          v-if="format === NumberFormat.Currency"
          class="lp-input-icon-wrapper compact xpl-4"
        >
          <lp-icon class="lp-input-icon" name="dollar" compact />
        </span>
        <div class="lp-input-stacked-values w-100">
          <input
            v-if="!preview"
            ref="input"
            type="number"
            :value="modelValue"
            class="lp-number-input-original"
            :disabled="disabled"
            :max="max"
            :min="min"
            :placeholder="placeholder"
            :step="step"
            v-bind="$attrs"
            @input="onChange"
            @keydown="onKeydown"
            @focus="focus = true"
            @blur="focus = false"
          />
          <span
            v-if="preview || !focus"
            ref="buffer"
            class="lp-number-input-buffer ly-row ly-start-center no-events"
            :class="!preview && 'ml-1'"
            @click="disabled ? null : wrapperFocused"
          >
            {{ formattedValue }}
          </span>
        </div>
        <span
          v-if="format === NumberFormat.Percentage"
          class="lp-input-icon-wrapper"
        >
          <lp-icon class="lp-input-icon" name="percentage" />
        </span>
      </div>
      <lp-select
        v-if="options && !preview"
        v-model="optionValueRef"
        :label-by="optionsLabelBy"
        :options="options"
        :track-by="optionsTrackBy"
        :disabled="disabled"
        trigger-only
        compact
        @update:model-value="handleUpdateModelValue"
      />
    </div>
    <span
      v-if="message"
      class="lp-input-message mt-half d-flex"
      :class="[
        message
          ? `lp-input-message-danger`
          : status && `lp-input-message-${status}`,
        disabled && `lp-input-message-disabled`,
      ]"
    >
      {{ message }}
    </span>
    <span v-if="!message" class="lp-input-message mt-half d-flex text-gray">
      {{ helper }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import {
  formatCurrency,
  formatDecimal,
  formatInteger,
  formatPhoneNumber,
  formatSSN,
  formatZip,
  NumberFormat,
} from '@/helpers/numbers'
import LpSelect from '@/components/select/LpSelect.vue'
import { t } from '@/plugins/i18n'

defineOptions({
  name: 'lp-number-input',
  inheritAttrs: false,
})
const emit = defineEmits(['update:model-value', 'update:optionValue'])

interface Props {
  min?: number
  max?: number
  maxLength?: number
  step?: number
  disableStep?: boolean
  pageStep?: number
  status?: string
  message?: string | null
  helper?: string | null
  placeholder?: string | null
  disabled?: boolean
  labelTop?: string | null
  modelValue: number | null | string
  optionValue?: number | null | string
  options?: unknown[] | undefined
  optionsTrackBy?: string
  optionsLabelBy?: string
  optionsAmountBy?: string
  optionsPlaceholder?: string
  format?: string | null
  required?: boolean | null
  preview?: boolean | null
  showOptionValue?: boolean | null
  compact?: boolean
  noFormat?: boolean
  canBeEmpty?: boolean
  fallbackNumber?: number
  allowDecimals?: boolean
  decimals?: number
}

const props = withDefaults(defineProps<Props>(), {
  min: -Infinity,
  max: 999999999,
  maxLength: 9,
  step: 1,
  disableStep: false,
  pageStep: 5,
  labelTop: null,
  status: 'neutral',
  message: null,
  helper: null,
  placeholder: null,
  disabled: false,
  modelValue: null,
  optionValue: null,
  options: undefined,
  optionsTrackBy: 'value',
  optionsLabelBy: 'name',
  optionsAmountBy: 'amount',
  optionsPlaceholder: t('Shared.SelectOption'),
  format: NumberFormat.Decimal,
  required: false,
  preview: false,
  showOptionValue: false,
  noFormat: false,
  canBeEmpty: true,
  fallbackNumber: 0,
  allowDecimals: false,
  decimals: 2,
})
const optionValueRef = ref(props.optionValue)
const optionLabelRef = ref(props.optionsPlaceholder)
const buffer = ref(null)
const focus = ref(false)
const input = ref<number | null>(null)

// common behavior
function calculateStepReturnValue(value: number) {
  return Number(
    value.toFixed(props.allowDecimals && props.decimals ? props.decimals : 10),
  )
}

function calculateStep(e: KeyboardEvent) {
  const { value: targetValue } = e.currentTarget as HTMLInputElement
  let value = Number(targetValue)

  if (props.disabled) return calculateStepReturnValue(value)

  const { key } = e

  switch (key) {
    case '-':
    case '+':
    case 'e': {
      e.preventDefault()
      return calculateStepReturnValue(value)
    }
  }

  const step = Number(props.step)
  const pageStep = Number(props.pageStep)

  switch (key.toLowerCase()) {
    case 'up':
    case 'arrowup':
      if (props.disableStep) {
        e.preventDefault()
        break
      }
      value += step
      if (value > props.max) {
        value = props.max
      }
      break
    case 'down':
    case 'arrowdown':
      if (props.disableStep) {
        e.preventDefault()
        break
      }
      value -= step
      if (value < props.min) {
        value = props.min
      }
      break
    case 'pageup':
      if (props.disableStep) {
        e.preventDefault()
        break
      }
      value += pageStep
      if (value > props.max) {
        value = props.max
      }
      break
    case 'pagedown':
      if (props.disableStep) {
        e.preventDefault()
        break
      }
      value -= pageStep
      if (value < props.min) {
        value = props.min
      }
      break
  }

  return calculateStepReturnValue(value)
}

const isMaxLengthLongerThanMax = props.max.toString().length <= props.maxLength

// input behavior
function formatValue(inputValue: string): number | null {
  const inputValueAsNumber = Number(inputValue)
  const minValue = Number.isFinite(props.min) ? props.min : props.fallbackNumber
  const emptyValue = props.canBeEmpty ? null : minValue
  let newVal = inputValue.trim() === '' ? emptyValue : inputValueAsNumber
  if (
    !isMaxLengthLongerThanMax &&
    props.max &&
    inputValueAsNumber > props.max
  ) {
    newVal = props.max
  }

  if (Number.isFinite(props.min) && inputValueAsNumber < props.min) {
    newVal = props.min
  }

  if (props.maxLength) {
    if (inputValue.length <= props.maxLength) {
      return newVal
    } else {
      return Number(`${newVal}`.slice(0, props.maxLength))
    }
  }

  return newVal
}

function onChange(e: Event) {
  const { value } = e.currentTarget as HTMLInputElement
  const formattedValue = formatValue(value)

  emit('update:model-value', formattedValue)
}

function onKeydown(e: KeyboardEvent) {
  emit('update:model-value', calculateStep(e))
}

const formattedValue = computed(() => {
  if (props.modelValue === null) return null
  if (props.noFormat) return props.modelValue
  const fValue = formatValue(`${props.modelValue}`)

  if (fValue) {
    switch (props.format) {
      case NumberFormat.Currency:
        return formatCurrency(fValue).replace('$', '')
      case NumberFormat.Integer:
        return formatInteger(fValue)
      case NumberFormat.Zip:
        return formatZip(fValue)
      case NumberFormat.SocialSecurity:
        return formatSSN(fValue)
      case NumberFormat.Decimal:
        return formatDecimal(fValue, props.decimals)
      case NumberFormat.Percentage:
        return formatDecimal(fValue, props.decimals)
      case NumberFormat.Phone:
        return formatPhoneNumber(fValue)
    }
  }
  return fValue
})

const wrapperFocused = () => {
  focus.value = true
  nextTick().then(() => {
    input.value?.focus()
  })
}

function handleUpdateModelValue(e, f) {
  optionLabelRef.value = f[props.optionsLabelBy]
  emit('update:model-value', f[props.optionsAmountBy])
}
</script>
